import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(() => localStorage.getItem('oriva_token'));
  const [error, setError] = useState(null);

  const getAuthHeaders = useCallback(() => {
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, [token]);

  const checkAuth = useCallback(async () => {
    if (!token) {
      setLoading(false);
      return;
    }
    
    try {
      const response = await axios.get(`${API_URL}/api/auth/me`, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        timeout: 15000
      });
      setUser(response.data);
      setError(null);
    } catch (err) {
      console.error('Auth check failed:', err);
      if (err.response?.status === 401) {
        localStorage.removeItem('oriva_token');
        setToken(null);
        setUser(null);
      }
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    setError(null);
    
    const maxRetries = 3;
    let lastError = null;
    
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        console.log(`Login attempt ${attempt}...`);
        
        const response = await axios.post(`${API_URL}/api/auth/login`, 
          { email, password },
          { 
            timeout: 20000,
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            }
          }
        );
        
        console.log('Login successful');
        const { token: newToken, ...userData } = response.data;
        localStorage.setItem('oriva_token', newToken);
        setToken(newToken);
        setUser(userData);
        return userData;
      } catch (err) {
        lastError = err;
        console.error(`Login attempt ${attempt} failed:`, err.message);
        
        // If it's a 401 (wrong credentials), don't retry
        if (err.response?.status === 401) {
          const message = err.response?.data?.detail || 'Yanlış email və ya şifrə';
          setError(message);
          throw new Error(message);
        }
        
        // Wait before retrying
        if (attempt < maxRetries) {
          await new Promise(resolve => setTimeout(resolve, attempt * 1500));
        }
      }
    }
    
    // All retries failed
    let message = 'Giriş uğursuz oldu';
    if (lastError) {
      if (lastError.code === 'ECONNABORTED' || lastError.message?.includes('timeout')) {
        message = 'Server cavab vermir. Bir az sonra yenidən cəhd edin.';
      } else if (lastError.code === 'ERR_NETWORK' || lastError.message === 'Network Error' || !lastError.response) {
        message = 'Server ilə əlaqə qurula bilmədi. Səhifəni yeniləyin.';
      } else if (lastError.response?.data?.detail) {
        message = lastError.response.data.detail;
      }
    }
    setError(message);
    throw new Error(message);
  };

  const logout = async () => {
    try {
      if (token) {
        await axios.post(`${API_URL}/api/auth/logout`, {}, {
          headers: { 'Authorization': `Bearer ${token}` },
          timeout: 5000
        });
      }
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      localStorage.removeItem('oriva_token');
      setToken(null);
      setUser(null);
      setError(null);
    }
  };

  const updateProfile = async (data) => {
    const response = await axios.put(`${API_URL}/api/auth/profile`, data, {
      headers: { 
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      timeout: 10000
    });
    setUser(response.data);
    return response.data;
  };

  const clearError = () => setError(null);

  return (
    <AuthContext.Provider value={{ 
      user, 
      loading, 
      token,
      error,
      login, 
      logout, 
      updateProfile,
      clearError,
      getAuthHeaders,
      isAuthenticated: !!user 
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
